import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { FiCalendar, FiMapPin, FiMenu, FiX, FiChevronDown } from "react-icons/fi";
import "./Navbar.css";

const Navbar = () => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);             // Desktop dropdown
  const [mobileEventsOpen, setMobileEventsOpen] = useState(false); // Mobile accordion
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  // Scroll handler for subtle shadow lift
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setIsMobileOpen(false);
    setEventsOpen(false);
    setMobileEventsOpen(false);
  }, [location.pathname]);

  // Close desktop dropdown on outside click (ignoring mobile drawer taps)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setEventsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <header className={`navbar-wrapper ${scrolled ? "is-scrolled" : ""}`}>
        <div className="nav-container">
          
          {/* Brand & Identity */}
          <div className="nav-brand">
            <Link to="/home" className="logo-link">
              <img src="/logo.jpg" alt="BizBaz Events" className="brand-logo" />
            </Link>

            {/* Event Badge */}
            <div className="event-badge-chip">
              <div className="chip-item">
                <FiCalendar className="chip-icon" />
                <span>11th, 12th & 13th Dec</span>
              </div>
              <span className="chip-divider">•</span>
              <div className="chip-item">
                <FiMapPin className="chip-icon" />
                <span>KSTVET, Off Gigiri Rd</span>
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="desktop-menu">
            <Link 
              to="/home" 
              className={`nav-item ${location.pathname === "/home" ? "active" : ""}`}
            >
              HOME
            </Link>
            
            <Link 
              to="/about" 
              className={`nav-item ${location.pathname === "/about" ? "active" : ""}`}
            >
              ABOUT
            </Link>

            {/* Desktop Dropdown Container */}
            <div 
              className="nav-item-dropdown"
              ref={dropdownRef}
              onMouseEnter={() => setEventsOpen(true)}
              onMouseLeave={() => setEventsOpen(false)}
            >
              <button 
                className={`dropdown-trigger ${eventsOpen ? "active" : ""}`} 
                onClick={() => setEventsOpen((prev) => !prev)}
                type="button"
              >
                EVENTS <FiChevronDown className={`chevron ${eventsOpen ? "rotate" : ""}`} />
              </button>
              
              <div className={`dropdown-panel ${eventsOpen ? "show" : ""}`}>
                <Link to="/events" className="dropdown-link">
                  <span className="link-title">FESTIVALS</span>
                  <span className="link-sub">Curated artisan & family markets</span>
                </Link>
                <Link to="/offroad" className="dropdown-link">
                  <span className="link-title">OFFROAD</span>
                  <span className="link-sub">Outdoor live experience series</span>
                </Link>
              </div>
            </div>

            <Link 
              to="/gallery" 
              className={`nav-item ${location.pathname === "/gallery" ? "active" : ""}`}
            >
              GALLERY
            </Link>

            <Link 
              to="/contact" 
              className={`nav-item ${location.pathname === "/contact" ? "active" : ""}`}
            >
              CONTACT
            </Link>
          </nav>

          {/* Action Zone */}
          <div className="nav-actions">
            <Link to="/contact" className="exhibit-link">
              EXHIBIT WITH US
            </Link>

            <button 
              className="mobile-toggle"
              onClick={() => setIsMobileOpen((prev) => !prev)}
              aria-label="Toggle Menu"
              type="button"
            >
              {isMobileOpen ? <FiX /> : <FiMenu />}
            </button>
          </div>

        </div>
      </header>

      {/* Mobile Overlay & Drawer */}
      <div 
        className={`mobile-overlay ${isMobileOpen ? "is-visible" : ""}`} 
        onClick={() => setIsMobileOpen(false)} 
      />
      
      <aside className={`mobile-drawer ${isMobileOpen ? "is-open" : ""}`}>
        <div className="drawer-header">
          <img src="/logo.jpg" alt="BizBaz Events" className="drawer-logo" />
          <button 
            className="drawer-close" 
            onClick={() => setIsMobileOpen(false)}
            type="button"
            aria-label="Close Menu"
          >
            <FiX />
          </button>
        </div>

        <div className="drawer-event-card">
          <div className="drawer-event-row">
            <FiCalendar className="card-icon" />
            <div>
              <strong>NEXT EVENT</strong>
              <p>11th, 12th & 13th Dec</p>
            </div>
          </div>
          <div className="drawer-event-row">
            <FiMapPin className="card-icon" />
            <div>
              <strong>LOCATION</strong>
              <p>KSTVET, Off Gigiri Rd</p>
            </div>
          </div>
        </div>

        <nav className="mobile-nav-list">
          <Link to="/home" className="mobile-nav-link">HOME</Link>
          <Link to="/about" className="mobile-nav-link">ABOUT</Link>
          
          {/* Isolated Mobile Accordion */}
          <div className="mobile-accordion">
            <button 
              className="accordion-trigger" 
              onClick={(e) => {
                e.stopPropagation();
                setMobileEventsOpen((prev) => !prev);
              }}
              type="button"
            >
              <span>EVENTS</span>
              <FiChevronDown className={`chevron ${mobileEventsOpen ? "rotate" : ""}`} />
            </button>
            
            {mobileEventsOpen && (
              <div className="accordion-content">
                <Link to="/events" className="accordion-link">FESTIVALS</Link>
                <Link to="/offroad" className="accordion-link">OFFROAD</Link>
              </div>
            )}
          </div>

          <Link to="/gallery" className="mobile-nav-link">GALLERY</Link>
          <Link to="/contact" className="mobile-nav-link">CONTACT</Link>
        </nav>

        <div className="drawer-footer">
          <Link to="/contact" className="exhibit-link mobile-full-width">
            EXHIBIT WITH US
          </Link>
        </div>
      </aside>
    </>
  );
};

export default Navbar;